import type { OptionDealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import type { WritableDraft } from 'immer';

/**
 * Option-only OTC field derivation: inverse payoff quote/delivery, index selection, payment currency.
 * Used by Redux (`OptionProductBuilderSliceStrategy.updateOTCFields`) and the OTC fields dialog local state.
 */
export type OptionOtcFieldsUpdateTarget = OptionDealProductBuilderRow | WritableDraft<OptionDealProductBuilderRow>;

export function applyOptionOtcFieldsUpdate(
  state: OptionOtcFieldsUpdateTarget,
  otcFields: OptionDealProductBuilderRow['otcFields'],
  fixingIndexMapping?: FixingIndexMappingMap
): void {
  if (otcFields == null) {
    state.otcFields = otcFields;
    return;
  }
  const currentPayoffType = state.otcFields?.payoffType;
  const newPayoffType = otcFields?.payoffType;
  const currentDeliveryAsset = state.otcFields?.deliveryAsset;
  const currentQuoteAsset = state.otcFields?.quoteAsset;
  const newQuoteAsset = otcFields?.quoteAsset;
  const currentIndex = state.otcFields?.index;
  const isPayoffTypeChanging = currentPayoffType !== newPayoffType;
  const isQuoteAssetChanging = currentQuoteAsset !== newQuoteAsset;

  // Shallow-copy payload into draft state. Action payloads can be frozen (e.g. RTK); assigning the
  // same reference then mutating deliveryAsset/index/paymentCcy below throws in strict mode.
  state.otcFields = {
    ...otcFields,
    ...(!otcFields.deliveryAsset && currentDeliveryAsset ? { deliveryAsset: currentDeliveryAsset } : {}),
  };

  // Handle payoff type changes
  if (isPayoffTypeChanging) {
    if (newPayoffType === 'Inverse') {
      // When switching to Inverse, quote and delivery both track underlying (PORT-18261)
      state.otcFields.quoteAsset = state.baseAsset || '';
      state.otcFields.deliveryAsset = state.baseAsset || undefined;
    } else if (newPayoffType === 'Linear') {
      // Clear delivery asset when switching TO Linear (regardless of previous type)
      state.otcFields.deliveryAsset = undefined;
      if (currentPayoffType === 'Inverse') {
        state.otcFields.quoteAsset = '';
      }
    }
  } else if (newPayoffType === 'Inverse' && state.baseAsset) {
    // Enforce quote = delivery = base whenever payoff stays Inverse
    state.otcFields.quoteAsset = state.baseAsset;
    state.otcFields.deliveryAsset = state.baseAsset;
  }

  const quoteAfterMutation = state.otcFields.quoteAsset;
  const payoffAfter = state.otcFields.payoffType;

  // Handle index auto-selection when quote/payoff context changes or when there's only one available index
  if (state.baseAsset && fixingIndexMapping) {
    const quoteAssetForIndexLookup =
      payoffAfter === 'Inverse'
        ? 'USD'
        : quoteAfterMutation === 'USDT' || quoteAfterMutation === 'USDC'
          ? 'USD'
          : quoteAfterMutation;

    if (quoteAssetForIndexLookup) {
      const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
        state.baseAsset,
        quoteAssetForIndexLookup,
        fixingIndexMapping
      );
      const availableIndexes = fixings
        .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
        .map(fixing => fixing.Name);

      const indexContextChanged =
        isPayoffTypeChanging ||
        (payoffAfter === 'Inverse' ? currentQuoteAsset !== quoteAfterMutation : isQuoteAssetChanging);

      // Auto-select if there's exactly one index available
      if (availableIndexes.length === 1) {
        state.otcFields.index = availableIndexes[0];
      } else if (indexContextChanged && currentIndex && availableIndexes.length > 0) {
        const currentIndexIsValid = availableIndexes.includes(currentIndex);
        if (!currentIndexIsValid) {
          state.otcFields.index = availableIndexes[0];
        }
      }
    }
  }

  // Compute and set paymentCcy based on payoffType
  if (state.otcFields) {
    if (state.otcFields.payoffType === 'Inverse') {
      state.otcFields.paymentCcy = state.baseAsset || undefined;
    } else if (state.otcFields.payoffType === 'Linear') {
      state.otcFields.paymentCcy = state.otcFields.quoteAsset || undefined;
    } else {
      // For other payoff types (e.g., Quanto), clear paymentCcy
      state.otcFields.paymentCcy = undefined;
    }
  }
}
